import React, { useState, useEffect, useRef } from "react";
import Moment from "react-moment";
import { replyToComment, deleteCommentImage, deleteReplyImage, deleteReply, deleteComment } from "../../functions/post";
import { uploadImages } from "../../functions/uploadImages";
import dataURItoBlob from "../../helpers/dataURItoBlob";
import { useSelector } from "react-redux";
import { useSocketContext } from "../../context/SocketContext";

export default function Comment({ comment, onDelete, postId, setComments, setCount }) {
  const { user } = useSelector((user) => ({ ...user }));
  const { socket, isConnected } = useSocketContext?.() || {};
  const commentId = comment.id || comment._id;
  

  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyImage, setReplyImage] = useState("");
  const [replies, setReplies] = useState(comment.replies || []);
  const [showReplies, setShowReplies] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedComment, setEditedComment] = useState(comment.comment);
  const [isDeleted, setIsDeleted] = useState(!!comment.isDeleted || (!comment.comment && !comment.image && !comment.gifUrl && !comment.stickerUrl));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [liked, setLiked] = useState(false);
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editedReplyText, setEditedReplyText] = useState("");
  const [showFullImage, setShowFullImage] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [commentImage, setCommentImage] = useState(comment.image || "");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingImage, setDeletingImage] = useState(false);
  const [replyDeleteConfirm, setReplyDeleteConfirm] = useState(null);
  
  const replyImageInputRef = useRef(null);
  const [openReplyMenuId, setOpenReplyMenuId] = useState(null);
  const replyMenuRefs = useRef({});

  // Close reply menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!openReplyMenuId) return;
      const el = replyMenuRefs.current[openReplyMenuId];
      if (el && !el.contains(e.target)) setOpenReplyMenuId(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openReplyMenuId]);



  useEffect(() => {
    // Initialize data
    setReplies(comment.replies || []);
  // keep replies hidden by default (user can click "View replies" to open)
    setCommentImage(comment.image || "");
    
    // Load other data from localStorage for backward compatibility
    const deletedComments = JSON.parse(localStorage.getItem("deletedComments")) || [];
    const editedComments = JSON.parse(localStorage.getItem("editedComments")) || {};
  const likeData = JSON.parse(localStorage.getItem("commentLikes")) || {};

  if (comment.isDeleted || deletedComments.includes(commentId)) setIsDeleted(true);
    if (editedComments[commentId]) setEditedComment(editedComments[commentId]);
    if (likeData[commentId]) setLiked(likeData[commentId].liked);
  }, [commentId, comment.replies, comment.image, comment.isDeleted, comment.comment, comment.gifUrl, comment.stickerUrl]);

  // Real-time: listen for reply deletions and update UI
  useEffect(() => {
    if (!socket || !isConnected) return;
    const handler = (payload) => {
      try {
        if (!payload) return;
        const { postId: pId, commentId: cId, replyId: rId } = payload;
        if (pId === postId && cId === commentId && rId) {
          setReplies((prev) => (prev || []).filter((r) => (r._id || r.id || r.tempId) !== rId));
        }
      } catch (e) {
        console.warn("Failed to handle reply-deleted event:", e?.message || e);
      }
    };
    socket.on('reply-deleted', handler);
    return () => {
      socket.off('reply-deleted', handler);
    };
  }, [socket, isConnected, postId, commentId]);

  // Real-time: listen for comment deletions and hide this comment if matching
  useEffect(() => {
    if (!socket || !isConnected) return;
    const handler = (payload) => {
      try {
        if (!payload) return;
        const { postId: pId, commentId: cId } = payload;
        if (pId === postId && cId === commentId) {
          setIsDeleted(true);
        }
      } catch (e) {
        console.warn("Failed to handle comment-deleted event:", e?.message || e);
      }
    };
    socket.on('comment-deleted', handler);
    return () => {
      socket.off('comment-deleted', handler);
    };
  }, [socket, isConnected, postId, commentId]);

  const saveEditedComment = (editedText) => {
    const allEdits = JSON.parse(localStorage.getItem("editedComments")) || {};
    allEdits[commentId] = editedText;
    localStorage.setItem("editedComments", JSON.stringify(allEdits));
  };

  const saveLikeState = (liked) => {
    const likeData = JSON.parse(localStorage.getItem("commentLikes")) || {};
    likeData[commentId] = { liked };
    localStorage.setItem("commentLikes", JSON.stringify(likeData));
  };

  const handleReplyClick = () => setReplying(!replying);

  const handleReplyImageUpload = (e) => {
    let file = e.target.files[0];
    
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif", "image/avif"];
    if (!allowedTypes.includes(file.type)) {
      setReplyError(`${file.name} format is not supported. Please use JPG, PNG, WEBP, GIF, or AVIF.`);
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 1024 * 1024 * 5) {
      setReplyError(`${file.name} is too large. Maximum size allowed is 5MB.`);
      return;
    }

    setReplyError(""); // Clear any previous errors

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      setReplyImage(event.target.result);
    };
  };

  const removeReplyImage = () => {
    setReplyImage("");
    if (replyImageInputRef.current) {
      replyImageInputRef.current.value = '';
    }
  };

  const handleReplySubmit = async () => {
    if ((!replyText.trim() && !replyImage) || isSubmitting) return;
    
    setIsSubmitting(true);
    setReplyError("");
    
    try {
      let imageUrl = "";
      
      if (replyImage) {
        const img = dataURItoBlob(replyImage);
        const path = `${comment.commentBy.username || 'user'}/post_images/${postId}`;
        let formData = new FormData();
        formData.append("path", path);
        formData.append("file", img);
        
        const imgReply = await uploadImages(formData, path, user.token);
        imageUrl = imgReply[0]?.url || "";
      }

      const result = await replyToComment(postId, comment._id, replyText, imageUrl, user.token);
      
      if (typeof result === "string") {
        setReplyError(result);
      } else {
        // Success - update replies
        setReplies(result);
        // Ensure replies are visible when a new reply is added
        setShowReplies(true);
        setReplyText("");
        setReplyImage("");
        setReplying(false);
      }
    } catch (error) {
      console.error("Reply submission error:", error);
      setReplyError("Failed to post reply. Please try again.");
    }
    
    setIsSubmitting(false);
  };

  const handleEditClick = () => setIsEditing(true);

  const handleEditSubmit = () => {
    if (!editedComment.trim()) return;
    saveEditedComment(editedComment);
    setIsEditing(false);
  };

  const handleDeleteClick = async () => {
    // Optimistic update
    setIsDeleted(true);
    const deletedComments = JSON.parse(localStorage.getItem("deletedComments")) || [];
    if (!deletedComments.includes(commentId)) {
      deletedComments.push(commentId);
      localStorage.setItem("deletedComments", JSON.stringify(deletedComments));
    }
    if (onDelete) onDelete(commentId);

    // Call API to delete comment so followers/devices get socket event
    try {
      const res = await deleteComment(postId, commentId, user.token);
      if (typeof res === 'string' && res?.toLowerCase?.().includes('fail')) {
        // Revert on failure
        setIsDeleted(false);
        if (onDelete) onDelete(null);
      }
    } catch (e) {
      // Revert on error
      setIsDeleted(false);
      if (onDelete) onDelete(null);
      console.error('Failed to delete comment:', e?.message || e);
    }
  };

  const handleLikeClick = () => {
    const newLiked = !liked;
    setLiked(newLiked);
    saveLikeState(newLiked);
  };

  const handleImageClick = () => {
    setShowFullImage(true);
  };

  const handleImageClose = () => {
    setShowFullImage(false);
  };

  const handleDeleteCommentImage = async () => {
    if (!commentImage) return;
    
    setDeletingImage(true);
    try {
      const result = await deleteCommentImage(postId, commentId, user.token);
      
      if (typeof result === "string") {
        console.error("Delete error:", result);
      } else {
        // Update comments in parent component
        setComments(result.comments);
        setCommentImage(""); // Remove image from local state
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      console.error("Error deleting comment image:", error);
    } finally {
      setDeletingImage(false);
    }
  };

  const handleDeleteReplyImage = async (replyId) => {
    setDeletingImage(true);
    try {
      const result = await deleteReplyImage(postId, commentId, replyId, user.token);
      
      if (typeof result === "string") {
        console.error("Delete reply image error:", result);
      } else {
        // Update replies in local state
        setReplies(result.replies);
        setReplyDeleteConfirm(null);
      }
    } catch (error) {
      console.error("Error deleting reply image:", error);
    } finally {
      setDeletingImage(false);
    }
  };

  const handleDeleteReply = async (replyId) => {
    try {
      const result = await deleteReply(postId, commentId, replyId, user.token);
      if (typeof result === 'string') {
        console.error('Delete reply error:', result);
        return;
      }
      const updated = result.replies || [];
      setReplies(updated);
      // If no replies left, hide the replies section
      if (updated.length === 0) setShowReplies(false);
    } catch (e) {
      console.error('Error deleting reply:', e?.message || e);
    }
  };


  // Render even if deleted to show replies, with a placeholder

  return (
    <div className="comment">
      {!isDeleted && comment.commentBy && (
        <img src={comment.commentBy.picture || ''} alt="" className="comment_img" />
      )}
      <div className="comment_col">
        {!isDeleted && comment.commentBy && (
          <div className="comment_wrap">
            <div className="comment_name">
              {comment.commentBy.first_name} {comment.commentBy.last_name}
            </div>

            {isEditing ? (
              <div className="edit_section">
                <input
                  type="text"
                  value={editedComment}
                  onChange={(e) => setEditedComment(e.target.value)}
                  className="edit_input"
                />
                <button
                  onClick={handleEditSubmit}
                  className="edit_submit_button"
                  disabled={!editedComment.trim()}
                >
                  Save
                </button>
              </div>
            ) : (
              editedComment ? (
                <div className="comment_text">{editedComment}</div>
              ) : null
            )}
          </div>
        )}

        {/* Enhanced image display for comments */}
  {!isDeleted && commentImage && (
          <div className="comment_image_container">
            <img 
              src={commentImage} 
              alt="Comment attachment" 
              className="comment_image"
              onClick={handleImageClick}
              style={{ cursor: 'pointer' }}
            />
          </div>
        )}

        {/* GIF display for comments */}
  {!isDeleted && comment.gifUrl && (
          <div className="comment_gif_container">
            <img 
              src={comment.gifUrl} 
              alt="GIF attachment" 
              className="comment_gif"
              style={{ cursor: 'pointer' }}
              onError={(e) => {
                console.error("Failed to load GIF:", comment.gifUrl);
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Sticker display for comments */}
  {!isDeleted && comment.stickerUrl && (
          <div className="comment_sticker_container">
            <span className="comment_sticker">{comment.stickerUrl}</span>
          </div>
        )}

        {/* Delete confirmation modal */}
        {showDeleteConfirm && (
          <div className="delete_confirm_overlay">
            <div className="delete_confirm_modal">
              <h3>Delete Image?</h3>
              <p>This will permanently delete the image from your comment. This action cannot be undone.</p>
              <div className="delete_confirm_actions">
                <button 
                  onClick={() => setShowDeleteConfirm(false)}
                  className="cancel_delete_btn"
                  disabled={deletingImage}
                >
                  Cancel
                </button>
                <button 
                  onClick={handleDeleteCommentImage}
                  className="confirm_delete_btn"
                  disabled={deletingImage}
                >
                  {deletingImage ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reply delete confirmation modal */}
        {replyDeleteConfirm && (
          <div className="delete_confirm_overlay">
            <div className="delete_confirm_modal">
              <h3>Delete Reply Image?</h3>
              <p>This will permanently delete the image from your reply. This action cannot be undone.</p>
              <div className="delete_confirm_actions">
                <button 
                  onClick={() => setReplyDeleteConfirm(null)}
                  className="cancel_delete_btn"
                  disabled={deletingImage}
                >
                  Cancel
                </button>
                <button 
                  onClick={() => handleDeleteReplyImage(replyDeleteConfirm)}
                  className="confirm_delete_btn"
                  disabled={deletingImage}
                >
                  {deletingImage ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Full screen image modal */}
        {showFullImage && commentImage && (
          <div className="image_modal_overlay" onClick={handleImageClose}>
            <div className="image_modal_content">
              <img src={commentImage} alt="Full size comment attachment" />
              <button 
                className="image_modal_close"
                onClick={handleImageClose}
              >
                ✕
              </button>
            </div>
          </div>
        )}

  {!isDeleted && (
  <div className="comment_actions">
          <span
            onClick={handleLikeClick}
            style={{ cursor: "pointer", color: liked ? "blue" : "inherit" }}
          >
            {liked ? <><span className="notranslate">💙</span> Liked</> : <><span className="notranslate">👍</span> Like</>}
          </span>
          <span onClick={handleReplyClick} style={{ cursor: "pointer" }}>
            <span className="notranslate">💬</span> Reply
          </span>
          {/* Only show Edit and Delete for comment author */}
          {comment.commentBy && comment.commentBy._id === user.id && (
            <>
              <span onClick={handleEditClick} style={{ cursor: "pointer" }}>
                ✏️ Edit
              </span>
              <span
                onClick={handleDeleteClick}
                style={{ cursor: "pointer", color: "red" }}
              >
                Delete
              </span>
            </>
          )}
          <span>
            <Moment fromNow interval={30}>{comment.commentAt}</Moment>
          </span>
  </div>
  )}

        {/* Enhanced reply input with image support */}
  {!isDeleted && replying && (
          <div className="reply_input_container">
            {replyError && (
              <div className="reply_error">
                <span className="error_text">{replyError}</span>
                <button onClick={() => setReplyError("")} className="error_close">✕</button>
              </div>
            )}
            
            <div className="reply_input">
              <input
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder={replyImage ? "Add a caption to your image..." : "✍️ Write a reply..."}
                className="reply_text_input"
                disabled={isSubmitting}
              />
              
              <input
                type="file"
                hidden
                ref={replyImageInputRef}
                accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
                onChange={handleReplyImageUpload}
              />
              
              <div className="reply_actions_buttons">
                <button
                  onClick={() => replyImageInputRef.current.click()}
                  className={`reply_image_btn ${replyImage ? 'has_image' : ''}`}
                  title="Add image"
                  disabled={isSubmitting}
                >
                  <span className="notranslate">📷</span>
                </button>
                
                <button
                  onClick={handleReplySubmit}
                  className="reply_submit_button"
                  disabled={(!replyText.trim() && !replyImage) || isSubmitting}
                >
                  {isSubmitting ? "Sending..." : "Reply"}
                </button>
              </div>
            </div>
            
            {/* Reply image preview */}
            {replyImage && (
              <div className="reply_image_preview">
                <img src={replyImage} alt="Reply preview" />
                <button
                  className="remove_reply_image"
                  onClick={removeReplyImage}
                  title="Remove image"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}

  {replies.length > 0 && showReplies && (
    <div className="replies">
            {[...replies].reverse().map((reply) => {
              const replyId = reply._id || reply.tempId || reply.id;
              const isEditingReply = editingReplyId === replyId;

              return (
                <div key={replyId} className="reply">
                  <img src={reply.commentBy?.picture || "/images/default_pic.png"} alt="" className="reply_img" />
                  <div className="reply_col">
                    <div className="reply_name">
                      {reply.commentBy?.first_name} {reply.commentBy?.last_name}
                    </div>

                    {isEditingReply ? (
                      <div className="edit_section">
                        <input
                          type="text"
                          value={editedReplyText}
                          onChange={(e) => setEditedReplyText(e.target.value)}
                          className="edit_input"
                        />
                        <button
                          className="reply_save_button"
                          onClick={async () => {
                            if (!editedReplyText.trim()) return;
                            // optimistic update
                            const prev = replies;
                            const updated = replies.map((r) =>
                              (r._id || r.tempId || r.id) === editingReplyId
                                ? { ...r, comment: editedReplyText }
                                : r
                            );
                            setReplies(updated);
                            setEditingReplyId(null);

                            try {
                              const { editReply } = await import("../../functions/post");
                              const res = await editReply(postId, comment._id, editingReplyId, editedReplyText, user.token);
                              if (res && res.replies) {
                                setReplies(res.replies);
                              }
                            } catch (e) {
                              // revert on error and show local update (we already reverted to prev below)
                              console.error("Failed to save edited reply:", e);
                              setReplies(prev);
                            } finally {
                              setEditedReplyText("");
                            }
                          }}
                          disabled={!editedReplyText.trim()}
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="reply_row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1 }} className="reply_text">{reply.comment}</div>

                          {reply.commentBy?._id === user.id && (
                            <div className="reply_more_container" ref={(el) => (replyMenuRefs.current[replyId] = el)} style={{ position: 'relative' }}>
                              <button
                                className="reply_more_button"
                                onClick={() => setOpenReplyMenuId(openReplyMenuId === replyId ? null : replyId)}
                                aria-haspopup="true"
                                aria-expanded={openReplyMenuId === replyId}
                                title="More"
                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: '4px' }}
                              >
                                ⋯
                              </button>

                              {openReplyMenuId === replyId && (
                                <div className="reply_more_menu" style={{ position: 'absolute', right: 0, top: '22px', zIndex: 50, background: '#fff', border: '1px solid #ddd', borderRadius: '6px', boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                                  <button
                                    className="reply_more_item"
                                    onClick={() => {
                                      setEditingReplyId(replyId);
                                      setEditedReplyText(reply.comment);
                                      setOpenReplyMenuId(null);
                                    }}
                                    style={{ display: 'block', padding: '6px 12px', width: '100%', textAlign: 'left', background: 'transparent', border: 'none' }}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="reply_more_item"
                                    onClick={() => {
                                      setOpenReplyMenuId(null);
                                      handleDeleteReply(replyId);
                                    }}
                                    style={{ display: 'block', padding: '6px 12px', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: 'red' }}
                                  >
                                    Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Display reply images with delete option */}
                        {reply.image && (
                          <div className="reply_image_container">
                            <img 
                              src={reply.image} 
                              alt="Reply attachment" 
                              className="reply_image"
                              style={{ 
                                marginTop: '5px',
                                width: '150px',
                                height: '100px',
                                borderRadius: '8px',
                                objectFit: 'cover',
                                cursor: 'pointer'
                              }}
                            />
                            {/* Delete reply image button - only show for reply owner */}
                            {reply.commentBy?._id === user.id && (
                              <button 
                                className="delete_reply_image_btn"
                                onClick={() => setReplyDeleteConfirm(replyId)}
                                title="Delete image"
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    <div className="reply_time">
                      <Moment fromNow>{reply.commentAt}</Moment>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* View/Hide replies toggle */}
        {replies.length > 0 && (
          <div className="view_replies_toggle" style={{ marginTop: '6px' }}>
            <button
              onClick={() => setShowReplies((s) => !s)}
              className="view_replies_button"
              style={{ background: 'transparent', border: 'none', color: '#6b6b6b', cursor: 'pointer', padding: 0 }}
            >
              {showReplies ? `Hide replies (${replies.length})` : `View replies (${replies.length})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}